import type {
  BasketPowerEfficiencyFlag,
  LineItemFragmentFragment,
  WishlistLineItemsFragmentFragment,
  WishlistProductEnergyEfficiency,
} from '@packages/gql/generated/shopping/graphql';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Link } from '@packages/modules/src/Link/Link';

function isBasketPowerEfficiencyFlag(data: any): data is BasketPowerEfficiencyFlag {
  return 'icon' in data && 'text' in data && 'level' in data;
}

function isWishlistProductEnergyEfficiency(data: any): data is WishlistProductEnergyEfficiency {
  return 'icon' in data && 'text' in data && 'level' in data;
}

const PowerEfficiencyElement: React.FC<{
  data: BasketPowerEfficiencyFlag | WishlistProductEnergyEfficiency;
  isTire?: boolean;
}> = ({ data, isTire }) => {
  const { icon, iconText, text, level, link, dataSheet } = data;
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div key={icon} className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        data-testid="eek-button"
        aria-label={`${text} ${level}`}
        className={
          isTire
            ? 'button border-color-text-dark-transparent focus-visible:outline-color-text-dark-transparent inline-flex items-center justify-center gap-1.5 border border-solid px-2 focus-visible:outline-2'
            : ''
        }
      >
        <Image // here we are distinguishing between the tireLabel since tirelabel has iconText and the other labels do not
          width={iconText ? '16' : '35'}
          height={iconText ? '16' : '20'}
          src={`${icon}.svg`}
          alt={`${text} ${level}`}
          data-testid="eek-icon"
          unoptimized
        />
        {iconText && <span className="text-sm whitespace-nowrap">{iconText}</span>}
      </button>
      {!isTire && dataSheet && (
        <div>
          <Link
            className="text-size-body-xs underline"
            href={dataSheet}
            target="_blank"
            rel="noreferrer"
          >
            <FormattedMessage
              key="LineItem.PowerEfficiency.DataSheet-text"
              id="LineItem.PowerEfficiency.DataSheet"
              defaultMessage="Produktdatenblatt"
            />
          </Link>
        </div>
      )}
      <Dialog
        isOpen={isOpen}
        title={`${text} ${level}`}
        onClose={() => setIsOpen(false)}
        onSubmit={() => setIsOpen(false)}
      >
        <DialogHeader onClose={() => setIsOpen(false)}>
          {text} {level}
        </DialogHeader>
        <DialogContent>
          <Image
            width={300}
            height={200}
            data-testid="eek-image"
            src={`${link}?$PDV_portrait_hq$`}
            alt={`${text} ${level}`}
            unoptimized
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const PowerEfficiency: React.FC<{
  efficiency:
    | LineItemFragmentFragment['product']['powerEfficiencyFlags']
    | WishlistLineItemsFragmentFragment['powerEfficiency']
    | null;
}> = ({ efficiency }) => {
  const isTire = efficiency?.some((data) => isBasketPowerEfficiencyFlag(data) && data.iconText);
  const containerClassName = isTire
    ? 'flex gap-1 flex-row flex-wrap items-center'
    : 'flex gap-1 flex-col items-start';

  if (efficiency) {
    return (
      <>
        <div className={containerClassName}>
          {efficiency
            .filter(
              (data) =>
                isBasketPowerEfficiencyFlag(data) || isWishlistProductEnergyEfficiency(data),
            )
            .map((data) => (
              <PowerEfficiencyElement
                key={`${data.text}_${data.level}`}
                data={data}
                isTire={isTire}
              />
            ))}
        </div>
        {isTire && efficiency[0].dataSheet && (
          <div>
            <Link
              className="text-size-body-xs underline"
              href={efficiency[0].dataSheet}
              target="_blank"
              rel="noreferrer"
            >
              <FormattedMessage
                key="LineItem.PowerEfficiency.DataSheet-text"
                id="LineItem.PowerEfficiency.DataSheet"
                defaultMessage="Produktdatenblatt"
              />
            </Link>
          </div>
        )}
      </>
    );
  }

  return null;
};
